import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { config } from "../../shared/environment";

//module
import { ClockInTpe, GetCurrentUser, GetStoreLocation, LogOutType, currentUserType, storeLocation, DisplaySettingsJson, Measurement, defaultRolesApiResponse, defaultRolesType, UserDetails, GetUserDetailsResponse, FetchPluginsResponse, Plugin, UpdateUserNotificationDetails, UpdateUserNotificationDetailsResponse, DeactivateAccountResponse, GetUserActivitiesResponse } from "./commonSliceType";
import { GetCountryName } from "../../components/customers/addEditCustomerModule";
import { CheckDuplicatesProps, CheckDuplicates } from "../../components/customerGroups/customerGroupModule";
import { RootState } from '../store'; // Adjust this import based on your project structure
import { currencyResponseProps, industryTypeResponseProps } from "../../components/settings/storeSetting/storeSettingModule";
import { signInResponse } from "./authSlice";
import { selectedLocationType } from "../../components/sellScreen/sellScreenModule";
import { getOneRoleResponse } from "../../components/role/roleModule";
import { IconType } from "zen-core-elements";

interface apiType {
    loading: boolean,
    error: string | null
}

interface changeEmailProps {
    "clientId": string,
    "userId": string,
    "storeId": number,
    "oldEmail": string,
    "newEmail": string,
    "oldNumber": number | null,
    "newNumber": number | null
}

interface ChangeMailResponse {
    "sendMail": boolean,
    "code": string,
    "message": string,
    "success": boolean
}

interface uploadLogoUrlResponse {
    "data": {
        "editProfileImage": {
            "settings": null,
            "msg": string,
            "statusCode": string,
            "success": boolean
        }
    }
}

interface uploadLogoUrlProps {
    deletedLogoUrl: string
    storeId: number
    userId: string
    isCompressed: boolean
    logoImage: {
        fileName: string,
        url: string,
        ext: string,
        image: string
    }
}

export interface clockInProps { storeId: number, locationId: number, staffId: string, clockIn?: string, timeZone: string, clockOut?: string }

interface initialState {
    isLandscape: boolean
    isPageDirty: boolean,
    isLoggedIn: boolean
    signInResponse: signInResponse | null,
    currentLocation: selectedLocationType
    industryDetails: { name: string }
    currentUser: apiType & { value: currentUserType | null },
    userDetails: apiType & { value: UserDetails | null },
    getCountry: apiType & { value: GetCountryName | null },
    industryTypeValue: apiType & { value: industryTypeResponseProps | null }
    currencies: apiType & { value: currencyResponseProps | null }
    checkDuplicate: apiType & { value: CheckDuplicates | null }
    getStoreLocations: apiType & { value: storeLocation[] },
    logOut: apiType & { value: LogOutType | null },
    isClockIn: apiType & { value: ClockInTpe | null }
    deviceMode: string,
    clockOut: boolean,
    displaySettings: { error: string, value: DisplaySettingsJson | null },
    displaySettingInActive: boolean
    defaultRoles: defaultRolesType | null,
    isSidebarExpanded: boolean,
    pluginDetails: apiType & { value: Plugin[] | null },
    changeEmail: apiType & { value: ChangeMailResponse | null },
    userActivities: apiType & { value: GetUserActivitiesResponse["userActivities"] | null },
    posUserRoleSAndPermissions: apiType & { value: getOneRoleResponse | null },
    barcodeValue: string | null
    countriesDropdownOptions: { label: string; value: number; id: number; countryCode: number; }[],
    drawerMenuList: MenuItem[] | null
}
export type SubMenuItem = {
    menuName: string;
    navigation: string;
    isRead?: boolean;
    isWrite?: boolean;
};

export type MenuItem = {
    menuName: string;
    navigation: string;
    iconName: string;
    iconType?: IconType;
    iconSize?: number;
    subMenu?: SubMenuItem[];
    isRead?: boolean;
    isWrite?: boolean;
};

//initial state
const initialState: initialState = {
    isLandscape: true,
    isPageDirty: false,
    isLoggedIn: false,
    signInResponse: null,
    currentLocation: { id: -1, name: "", timeZone: "" },
    industryDetails: { name: "" },
    currentUser: { error: null, loading: true, value: null },
    userDetails: { error: null, loading: true, value: null },
    getCountry: { error: null, loading: true, value: null },
    industryTypeValue: { error: null, loading: true, value: null },
    currencies: { error: null, loading: true, value: null },
    checkDuplicate: { error: null, loading: false, value: null },
    getStoreLocations: { error: null, loading: true, value: [] },
    logOut: { error: null, loading: false, value: null },
    isClockIn: { error: null, loading: true, value: null },
    pluginDetails: { error: null, loading: true, value: null },
    changeEmail: { error: null, loading: true, value: null },
    userActivities: { error: null, loading: true, value: null },
    posUserRoleSAndPermissions: { error: null, loading: true, value: null },
    deviceMode: "",
    clockOut: false,
    displaySettings: { error: '', value: null },
    displaySettingInActive: false,
    defaultRoles: null,
    isSidebarExpanded: false,

    barcodeValue: null,
    countriesDropdownOptions: [],
    drawerMenuList: null,
}

//get current user
export const getCurrentUser = createAsyncThunk("common/getCurrentUser", () => {
    return HttpRoutingService?.getMethod<GetCurrentUser>(API?.getCurrentUser, null, "v1/")
})
export const getUserDetails = createAsyncThunk("common/getUserDetails", ({ userId, storeId }: { userId: string, storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getUserDetails, { storeId, userId: userId })
    return HttpRoutingService?.getMethod<GetUserDetailsResponse>(url, null, "v1/")
})
export const updateUserNotificationDetails = createAsyncThunk("common/updateUserDetails", ({ userId, storeId, data }: { userId: string, storeId: number, data: UpdateUserNotificationDetails }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getUserDetails, { storeId, userId: userId })
    return HttpRoutingService?.putMethod<UpdateUserNotificationDetailsResponse>(url, data, null, "v1/")
})
export const getOnePluginDetails = createAsyncThunk("common/getOnePluginDetails", ({ storeId, data }: { storeId: number, data: { pluginCode: string } }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePluginDetails, { storeId })
    return HttpRoutingService?.getMethod<FetchPluginsResponse>(url, data, "v1/")
})

export const changeEmail = createAsyncThunk("common/changeEmail", ({ userId, storeId, data }: { userId: string, storeId: number, data: changeEmailProps }) => {
    return HttpRoutingService?.postMethod<ChangeMailResponse>(API.changeEmail, data)
})

export const uploadLogoUrl = createAsyncThunk("common/uploadLogoUrl", ({ userId, storeId, data }: { userId: string, storeId: number, data: uploadLogoUrlProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.uploadLogoUrl, { storeId })
    return HttpRoutingService?.putMethod<uploadLogoUrlResponse>(url, data)
})

//get country
export const getCountry = createAsyncThunk("common/getCountry", (currency?: boolean) => {
    return HttpRoutingService?.getMethod<GetCountryName>('countries', { currency: currency ?? false })
})
//get all industry names
export const industryTypesName = createAsyncThunk("storeSettings/getAllPosIndustryTypesName", () => {
    return HttpRoutingService?.getMethod<industryTypeResponseProps>('industry/name')
})
//get all pos currencies
export const currencies = createAsyncThunk("storeSettings/getAllPosCurrencies", () => {
    return HttpRoutingService?.getMethod<currencyResponseProps>('countries/currencies')
})
export const getMeasurement = createAsyncThunk("storeSettings/getMeasurement", (storeId: number) => {
    const url = HttpRoutingService?.replaceUrl(API?.getMeasurement, { storeId: storeId })
    return HttpRoutingService?.getMethod<Measurement>(url, null, "v1/")
})
//get store location
export const getStoreLocation = createAsyncThunk("common/getStoreLocation", ({ storeId }: { storeId: number }) => {
    let url = HttpRoutingService?.replaceUrl(API?.getLocationList, { storeId })
    return HttpRoutingService?.getMethod<GetStoreLocation>(url)
})

export const logOutCurrentUser = createAsyncThunk("common/logOutCurrentUser", ({ data }: { data: { infoId: number | undefined } }) => {
    return HttpRoutingService?.postMethod<LogOutType>(API.logoutUser, data)
})
export const createAttendance = createAsyncThunk("common/clockIn", ({ storeId, data }: { storeId: number, data: clockInProps }, { getState }) => {
    const state = getState() as RootState; // Access the full Redux state
    const clockOut = state.common.clockOut; // Access the clockOut state
    const url = HttpRoutingService.replaceUrl(clockOut ? API.clockOut : API.clockIn, { storeId: storeId })
    return HttpRoutingService.postMethod<ClockInTpe>(url, data, null, 'v2/')
})

export const getDisplaySetting = createAsyncThunk("common/displaySettings", ({ storeId }: { storeId: number }) => {
    let url = HttpRoutingService.replaceUrl(API.getDisplayData, { storeId })
    return HttpRoutingService.getMethod<DisplaySettingsJson>(url, {})
})

export const getDefaultPOSRoles = createAsyncThunk("common/getDefaultPOSRoles", ({ storeId }: { storeId: number }) => {
    let url = HttpRoutingService.replaceUrl(API.defaultRoles, { storeId })
    return HttpRoutingService.getMethod<defaultRolesApiResponse>(url)
})

export const deactivateAccount = createAsyncThunk("common/deactivateAccount", ({ data }: { data: { userId: string, email: string, storeId: number } }) => {
    return HttpRoutingService.postMethod<DeactivateAccountResponse>(API.deactivateAccount, data)
})

export const getUserActivities = createAsyncThunk("common/getUserActivities", ({ storeId, userId }: { storeId: number, userId: string }) => {
    const url = HttpRoutingService.replaceUrl(API.userActivities, { storeId, userId })
    return HttpRoutingService.getMethod<GetUserActivitiesResponse>(url)
})

export const getPosUserRolesAndPermissions = createAsyncThunk("common/getPosUserRolesAndPermissions", ({ storeId, id }: { storeId: number, id: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosRole, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<getOneRoleResponse>(url)
})

interface checkDuplicatePayload {
    storeId: number,
    data: CheckDuplicatesProps
}

//check duplicate
export const checkDuplicatesRedux = createAsyncThunk("common/checkDuplicates", ({ storeId, data }: checkDuplicatePayload) => {
    const url = HttpRoutingService?.replaceUrl(API?.checkDuplicate, { storeId: storeId })
    return HttpRoutingService?.postMethod<{ "result": { "alreadyExists": boolean }, "success": boolean }>(url, data)
})

export const CommonSlice = createSlice({
    name: "common",
    initialState,
    reducers: {
        setLandscape: (state, action: PayloadAction<boolean>) => {
            state.isLandscape = action?.payload
        },
        pageDirty: (state, action: PayloadAction<boolean>) => {
            state.isPageDirty = action?.payload
        },
        loginState: (state, action: PayloadAction<boolean>) => {
            if (action?.payload == false) {
                state.currentUser.value = null
            }
            state.currentUser.loading = false
            state.isLoggedIn = action?.payload
        },
        updateCurrentUser: (state, action: PayloadAction<currentUserType>) => {
            state.currentUser.value = action?.payload
        },
        setCurrentLocation: (state, action: PayloadAction<{ id: number, name: string, timeZone: string }>) => {
            state.currentLocation = action.payload
        },
        setIndustryDetails: (state, action: PayloadAction<{ name: string }>) => {
            state.industryDetails = action.payload
        },
        setDeviceMode: (state, action: PayloadAction<string>) => {
            state.deviceMode = action.payload
        },
        setClockOutDevice: (state, action: PayloadAction<boolean>) => {
            state.clockOut = action.payload
        },
        setSignInResponse: (state, actions: PayloadAction<signInResponse>) => {
            state.signInResponse = actions?.payload
        },
        setDisplaySettings: (state, action: PayloadAction<boolean>) => {
            state.displaySettingInActive = action.payload
        },
        setSidebarExpanded: (state, action: PayloadAction<boolean>) => {
            state.isSidebarExpanded = action.payload
        },
        setBarcodeValue: (state, action: PayloadAction<string | null>) => {
            state.barcodeValue = action.payload
        },
        setDrawerMenuList: (state, action: PayloadAction<MenuItem[] | null>) => {
            state.drawerMenuList = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(currencies?.pending, (state) => {
                state.currencies.loading = true
            })
            .addCase(currencies?.fulfilled, (state, action) => {
                state.currencies.loading = false
                state.currencies.value = action.payload
            })
            .addCase(currencies?.rejected, (state, action) => {
                state.currencies.loading = false
                state.currencies.error = action?.error?.message as string
            })
            .addCase(changeEmail?.pending, (state) => {
                state.changeEmail.loading = true
            })
            .addCase(changeEmail?.fulfilled, (state, action) => {
                state.changeEmail.loading = false
                state.changeEmail.value = action.payload
            })
            .addCase(changeEmail?.rejected, (state, action) => {
                state.changeEmail.loading = false
                state.changeEmail.error = action?.error?.message as string
            })
            .addCase(getCurrentUser.pending, (state) => {
                state.currentUser.loading = true
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.currentUser.value = action?.payload?.currentUser
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.currentUser.error = action?.error?.message ? action?.error?.message : "get Current User Error "
            })
            .addCase(getUserDetails.pending, (state) => {
                state.userDetails.loading = true
            })
            .addCase(getUserDetails.fulfilled, (state, action) => {
                state.userDetails.value = action?.payload?.userDetails
            })
            .addCase(getUserDetails.rejected, (state, action) => {
                state.userDetails.error = action?.error?.message ? action?.error?.message : "get Current User Error "
            })
            .addCase(getOnePluginDetails.pending, (state) => {
                state.pluginDetails.loading = true
            })
            .addCase(getOnePluginDetails.fulfilled, (state, action) => {
                state.pluginDetails.value = action?.payload?.result
                state.pluginDetails.loading = false
            })
            .addCase(getOnePluginDetails.rejected, (state, action) => {
                state.pluginDetails.error = action?.error?.message ? action?.error?.message : "get Plugin Details Error "
                state.pluginDetails.loading = false
            })
            .addCase(getUserActivities.pending, (state) => {
                state.userActivities.loading = true
            })
            .addCase(getUserActivities.fulfilled, (state, action) => {
                state.userActivities.value = action?.payload?.userActivities
                state.userActivities.loading = false
            })
            .addCase(getUserActivities.rejected, (state, action) => {
                state.userActivities.error = action?.error?.message ? action?.error?.message : "get User Activities Error "
                state.userActivities.loading = false
            })
            .addCase(getCountry.pending, (state) => {
                state.getCountry.loading = true
            })
            .addCase(getCountry.fulfilled, (state, action) => {
                state.getCountry.loading = false
                state.getCountry.value = action?.payload
                state.countriesDropdownOptions = (action.payload?.country ?? []).map(item => ({
                    value: item.id,
                    label: item.name,
                    countryCode: item.countryCode,
                    id: item.id,
                }))
            })
            .addCase(getCountry.rejected, (state, action) => {
                state.getCountry.loading = false
                state.getCountry.error = action?.error?.message ? action?.error?.message : "get country Error "
            })
            .addCase(industryTypesName?.pending, (state) => {
                state.industryTypeValue.loading = true
            })
            .addCase(industryTypesName?.fulfilled, (state, action) => {
                state.industryTypeValue.loading = false
                state.industryTypeValue.value = action.payload
            })
            .addCase(industryTypesName?.rejected, (state, action) => {
                state.industryTypeValue.loading = false
                state.industryTypeValue.error = action?.error?.message!!
            })
            .addCase(checkDuplicatesRedux.pending, (state) => {
                state.checkDuplicate.loading = true
            })
            .addCase(checkDuplicatesRedux.fulfilled, (state, action) => {
                state.checkDuplicate.loading = false
                state.checkDuplicate.value = action?.payload
            })
            .addCase(checkDuplicatesRedux.rejected, (state, action) => {
                state.checkDuplicate.loading = false
                state.checkDuplicate.error = action?.error?.message ? action?.error?.message : "check duplicate Error "
            })
            .addCase(getStoreLocation.pending, (state) => {
                state.getStoreLocations.loading = true
            })
            .addCase(getStoreLocation.fulfilled, (state, action) => {
                state.getStoreLocations.loading = false
                state.getStoreLocations.value = action?.payload?.storeLocation
            })
            .addCase(getStoreLocation.rejected, (state, action) => {
                state.getStoreLocations.loading = false
                state.getStoreLocations.error = action?.error?.message ? action?.error?.message : "get store location Error "
            })
            .addCase(logOutCurrentUser.pending, (state, action) => {
                state.logOut.loading = true
            })
            .addCase(logOutCurrentUser.fulfilled, (state, action) => {
                state.logOut.loading = false
                state.logOut.value = action.payload
            })
            .addCase(logOutCurrentUser.rejected, (state, action) => {
                state.logOut.error = action.error.message ? action.error.message : "logOutCurrentUser error"

            })
            .addCase(getPosUserRolesAndPermissions.pending, (state, action) => {
                state.posUserRoleSAndPermissions.loading = true
            })
            .addCase(getPosUserRolesAndPermissions.fulfilled, (state, action) => {
                state.posUserRoleSAndPermissions.loading = false
                state.posUserRoleSAndPermissions.value = action.payload
            })
            .addCase(getPosUserRolesAndPermissions.rejected, (state, action) => {
                state.posUserRoleSAndPermissions.error = action.error.message ? action.error.message : "posUserRoleSAndPermissions error"

            })
            .addCase(createAttendance.fulfilled, (state, action) => {
                state.isClockIn.loading = false
                state.isClockIn.value = action.payload
            })
            .addCase(getDisplaySetting.fulfilled, (state, action) => {
                state.displaySettings.value = action.payload
            })
            .addCase(getDisplaySetting.rejected, (state, action) => {
                state.displaySettings.error = action.error.message as string
            })
            .addCase(getDefaultPOSRoles.fulfilled, (state, action) => {
                state.defaultRoles = action.payload.roles
            })
    }
})

export const { setDrawerMenuList, pageDirty, loginState, setCurrentLocation, updateCurrentUser, setIndustryDetails, setDeviceMode, setClockOutDevice, setSignInResponse, setDisplaySettings, setLandscape, setSidebarExpanded, setBarcodeValue } = CommonSlice?.actions

export default CommonSlice?.reducer